import {DateTime} from 'luxon';
import type {IFilterService} from 'angular';
import type {LocalizationService} from '../providers/localization.provider';

export type JpDateFilter = (date: number | string, format: string) => string | null

/**
 * @ngdoc filter
 * @name jpDate
 * @module portaro.filters
 * @kind function
 *
 * @param {number|string} date Input string. Filter uses a standard [Date](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date)
 * constructor to create a date value for formatting.
 * @param {string} format Standard {@link ng.filter.date angular date} filter format
 *
 * @return {string|null} Formatted date
 *
 * @description
 * Filter formats date with a given format. If the date is today, the date will be replaced message from `Today`. Similarly, for yesterday date.
 * If the date is not defined, returns `null`.
 */

/*@ngInject*/
export default function jpDateFilter($filter: IFilterService, localizationService: LocalizationService): JpDateFilter {

    const formatDate = (date: number | string, format: string) => {
        const lastIndexOfDate = Math.max(format.lastIndexOf('d'), format.lastIndexOf('M'), format.lastIndexOf('y'));
        if (lastIndexOfDate === -1) {
            return '';
        }
        const dateFormat = format.substring(0, lastIndexOfDate + 1);
        if (dateFormat.length === 0) {
            return '';
        }

        const l_date = DateTime.fromJSDate(new Date(date));
        const currentDate = DateTime.fromJSDate(new Date());

        if (currentDate.hasSame(l_date, 'day')) {
            return localizationService.get(/* @kp-localization commons.Dnes */ 'commons.Dnes');
        }

        if (currentDate.minus({days: 1}).hasSame(l_date, 'day')) {
            return localizationService.get(/* @kp-localization commons.Vcera */ 'commons.Vcera');
        }

        return $filter('date')(date, dateFormat);
    };

    const formatSpace = (date: number | string, format: string) => {
        if (format.indexOf(' ') !== -1) {
            return ' ';
        }
        return '';
    };

    const formatTime = (date: number | string, format: string) => {
        let firstIndexOfTime = 9999;
        if (format.indexOf('H') !== -1) {
            firstIndexOfTime = Math.min(firstIndexOfTime, format.indexOf('H'));
        }
        if (format.indexOf('m') !== -1) {
            firstIndexOfTime = Math.min(firstIndexOfTime, format.indexOf('m'));
        }
        if (format.indexOf('s') !== -1) {
            firstIndexOfTime = Math.min(firstIndexOfTime, format.indexOf('s'));
        }
        if (firstIndexOfTime === 9999) {
            return '';
        }
        const timeFormat = format.substring(firstIndexOfTime, format.length);
        if (timeFormat.length === 0) {
            return '';
        }
        return $filter('date')(date, timeFormat);
    };

    return (date: number | string, format: string) => {
        if (!date) {
            return null;
        }
        return formatDate(date, format) + formatSpace(date, format) + formatTime(date, format);
    };
}

jpDateFilter.filterName = 'jpDate';
